import type { CoreData, NameEntry, SexPref } from "../data/types";
import { TAG } from "../data/types";
import { SEX_WORD, decodeSpark, fmt, genderClass, genderLabel, sexIdxFor, trendChange, trendInfo } from "../data/format";
import { Sparkline } from "./charts";
import { IconArrow } from "./icons";

export function nameSizeClass(name: string): string {
  const n = name.length;
  if (n <= 5) return "nm-xl";
  if (n <= 7) return "nm-l";
  if (n <= 9) return "nm-m";
  if (n <= 12) return "nm-s";
  return "nm-xs";
}

export function tagChips(e: NameEntry): { label: string; cls: string }[] {
  const out: { label: string; cls: string }[] = [];
  if (e.tags & TAG.NEW) out.push({ label: "New arrival", cls: "chip-new" });
  if (e.tags & TAG.VINTAGE) out.push({ label: "Vintage revival", cls: "chip-vintage" });
  if (e.tags & TAG.TIMELESS) out.push({ label: "Timeless", cls: "chip-classic" });
  else if (e.tags & TAG.CLASSIC) out.push({ label: "Classic", cls: "chip-classic" });
  if (e.tags & TAG.UNISEX) out.push({ label: "Unisex", cls: "chip-unisex" });
  if (e.tags & TAG.SCOTLAND) out.push({ label: "Big in Scotland", cls: "chip-region" });
  if (e.tags & TAG.NI) out.push({ label: "Big in N. Ireland", cls: "chip-region" });
  return out;
}

export function NameCard({
  e,
  pref,
  core,
  hint,
  hintTaste,
  also,
}: {
  e: NameEntry;
  pref: SexPref;
  core: CoreData;
  /** e.g. "Alex likes this" when partner hints are on, or "Because you liked Margot" */
  hint?: string | null;
  /** a hint from your own likes rather than your partner's */
  hintTaste?: boolean;
  /** the name's other spellings, which the deck doesn't deal separately */
  also?: string[];
}) {
  const si = sexIdxFor(e, pref);
  const rank = e.r[si];
  const count = e.c[si];
  const trend = trendInfo(e.t[si]);
  const spark = decodeSpark(e.s[si] || e.s[si ? 0 : 1]);
  const g = genderClass(e);

  // three bands: who/where it ranks, the name itself, and how it's doing. The card is a size
  // container, so on short cards the lower-priority pieces step aside instead of overlapping.
  return (
    <div class={`card-face g-${g}`}>
      <div class="cf-top">
        <span class="cf-gender">
          <i class="gdot" aria-hidden="true" />
          {genderLabel(e)}
        </span>
        {rank ? (
          <span class="cf-rank" title={`Rank among ${SEX_WORD[si]} in ${core.latest}`}>
            <b>#{fmt(rank)}</b> {SEX_WORD[si]} · {core.latest}
          </span>
        ) : (
          <span class="cf-rank rare">Rare</span>
        )}
      </div>

      <div class="cf-center">
        <h2 class={`cf-name ${nameSizeClass(e.name)}`}>{e.name}</h2>
        <p class="cf-sub">
          {e.pron ? (
            <span class="pron" title="How to say it">
              {e.pron}
            </span>
          ) : (
            `${e.syl} syllable${e.syl === 1 ? "" : "s"}`
          )}
          {e.origin ? ` · ${e.origin}` : ""}
        </p>
        {also && also.length > 0 && <p class="cf-also">Also {spellingList(also)}</p>}
        {e.meaning ? (
          <p class="cf-meaning">
            “{e.meaning}”
          </p>
        ) : (
          e.originNote && (
            <p class="cf-meaning cf-origin-note">
              {e.originNote}
            </p>
          )
        )}
        {hint && <p class={hintTaste ? "cf-hint taste" : "cf-hint"}>{hint}</p>}
      </div>

      <div class="cf-bottom">
        {e.peakCount >= 15 && (
          <div class="cf-spark">
            <Sparkline values={spark} height={40} label={`Babies per year, ${core.y0} to ${core.latest}: ${trend.label.toLowerCase()}`} />
          </div>
        )}
        <div class="cf-stats">
          <div class="cf-stat">
            <b>{count ? fmt(count) : "Fewer than 3"}</b>
            <span>
              {SEX_WORD[si]} in {core.latest}
            </span>
          </div>
          {e.peakCount >= 15 ? (
            <div class={`cf-stat end t-${trend.dir}`}>
              <b>
                {trend.dir !== "none" && <IconArrow dir={trend.dir} size={15} />}
                {trend.label}
              </b>
              <span>{trendChange(e.t[si])}</span>
            </div>
          ) : (
            <div class="cf-stat end">
              <b>A rarity</b>
              <span>never above {e.peakCount} a year</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** "Sofia", "Sofia and Sofiia", "Sofia, Sofiia and 2 more" */
function spellingList(names: string[]): string {
  if (names.length === 1) return names[0];
  if (names.length <= 3) return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  return `${names.slice(0, 2).join(", ")} and ${names.length - 2} more`;
}
